import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { ProjectTypeDtoIn } from '@/models/in/ProjectTypeDtoIn';
import { ProjectTypeDtoOut } from '@/models/out/ProjectTypeDtoOut';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';

const QUERY_URL = 'management/project-types';

interface EditOrCreateProjectTypeDialogProps {
  open: boolean;
  onClose: () => void;
  typeToEdit?: ProjectTypeDtoIn | null;
  onSuccess: () => void;
}

const EditOrCreateProjectTypeDialog = ({
  open,
  onClose,
  typeToEdit,
  onSuccess,
}: EditOrCreateProjectTypeDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!typeToEdit;

  useEffect(() => {
    if (open) {
      setName(typeToEdit?.name ?? '');
      setDescription(typeToEdit?.description ?? '');
      setError(null);
    }
  }, [open, typeToEdit]);

  const handleClose = () => {
    setName('');
    setDescription('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('projectTypesManagement.dialog.nameRequired'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const body: ProjectTypeDtoOut = {
      name: name.trim(),
      description: description.trim() || null,
    };

    try {
      if (isEditing) {
        await putAuth<ProjectTypeDtoIn, object, ProjectTypeDtoOut>(
          `${QUERY_URL}/${typeToEdit!.id}`, headers, {}, body
        );
      } else {
        await postAuth<ProjectTypeDtoIn, ProjectTypeDtoOut>(QUERY_URL, headers, body);
      }
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('already exists')) {
        setError(t('projectTypesManagement.dialog.duplicateError'));
      } else {
        setError(t(isEditing
          ? 'projectTypesManagement.dialog.errorUpdating'
          : 'projectTypesManagement.dialog.errorCreating'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={isEditing
        ? t('projectTypesManagement.dialog.editTitle')
        : t('projectTypesManagement.dialog.createTitle')
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="pt-name" className="body-sm-semibold text-(--font-secondary)">
            {t('projectTypesManagement.name')}
          </label>
          <input
            id="pt-name"
            type="text"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            placeholder={t('projectTypesManagement.dialog.namePlaceholder')}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="pt-description" className="body-sm-semibold text-(--font-secondary)">
            {t('projectTypesManagement.description')}
          </label>
          <textarea
            id="pt-description"
            rows={3}
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('projectTypesManagement.dialog.descriptionPlaceholder')}
          />
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateProjectTypeDialog;
